import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, Sun, RotateCcw, Image as ImageIcon, Info } from 'lucide-react';

type SepiaTone = 'classic' | 'warm' | 'cool' | 'antique';
type OutputFormat = 'png' | 'jpg';

interface ToneConfig {
  value: SepiaTone;
  label: string;
  description: string;
}

const TONES: ToneConfig[] = [
  { value: 'classic', label: 'Classic Sepia', description: 'Traditional warm brown tone' },
  { value: 'warm', label: 'Warm Sepia', description: 'Extra warmth with more red' },
  { value: 'cool', label: 'Cool Sepia', description: 'Subtle blue undertones' },
  { value: 'antique', label: 'Antique', description: 'Low saturation with yellow cast' },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const SepiaImage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [intensity, setIntensity] = useState(80);
  const [tone, setTone] = useState<SepiaTone>('classic');
  const [vignetteEnabled, setVignetteEnabled] = useState(false);
  const [vignetteIntensity, setVignetteIntensity] = useState(50);
  const [grainEnabled, setGrainEnabled] = useState(false);
  const [grainIntensity, setGrainIntensity] = useState(30);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [jpgQuality, setJpgQuality] = useState(92);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ w: number; h: number } | null>(null);
  const [showBeforeAfter, setShowBeforeAfter] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setOriginalPreview(url);
      const img = new window.Image();
      img.onload = () => {
        setImgElement(img);
        setImageDimensions({ w: img.naturalWidth, h: img.naturalHeight });
      };
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const getSepiaMatrix = useCallback((t: SepiaTone) => {
    switch (t) {
      case 'classic':
        return {
          rr: 0.393, rg: 0.769, rb: 0.189,
          gr: 0.349, gg: 0.686, gb: 0.168,
          br: 0.272, bg: 0.534, bb: 0.131,
        };
      case 'warm':
        return {
          rr: 0.44, rg: 0.77, rb: 0.2,
          gr: 0.35, gg: 0.69, gb: 0.17,
          br: 0.24, bg: 0.5, bb: 0.12,
        };
      case 'cool':
        return {
          rr: 0.35, rg: 0.72, rb: 0.2,
          gr: 0.33, gg: 0.67, gb: 0.18,
          br: 0.3, bg: 0.58, bb: 0.16,
        };
      case 'antique':
        return {
          rr: 0.38, rg: 0.75, rb: 0.19,
          gr: 0.36, gg: 0.71, gb: 0.17,
          br: 0.28, bg: 0.54, bb: 0.13,
        };
    }
  }, []);

  const applySepia = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = img.naturalWidth;
    const h = img.naturalHeight;
    canvas.width = w;
    canvas.height = h;

    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;
    const matrix = getSepiaMatrix(tone);
    const blend = intensity / 100;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i + 1], b = data[i + 2];

      const sr = Math.min(255, matrix.rr * r + matrix.rg * g + matrix.rb * b);
      const sg = Math.min(255, matrix.gr * r + matrix.gg * g + matrix.gb * b);
      const sb = Math.min(255, matrix.br * r + matrix.bg * g + matrix.bb * b);

      data[i] = r + (sr - r) * blend;
      data[i + 1] = g + (sg - g) * blend;
      data[i + 2] = b + (sb - b) * blend;
    }

    // Apply grain
    if (grainEnabled && grainIntensity > 0) {
      const amount = grainIntensity * 0.8;
      for (let i = 0; i < data.length; i += 4) {
        const noise = (Math.random() - 0.5) * amount;
        data[i] = Math.max(0, Math.min(255, data[i] + noise));
        data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise));
        data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise));
      }
    }

    ctx.putImageData(imageData, 0, 0);

    // Apply vignette
    if (vignetteEnabled && vignetteIntensity > 0) {
      const cx = w / 2;
      const cy = h / 2;
      const maxDist = Math.sqrt(cx * cx + cy * cy);
      const gradient = ctx.createRadialGradient(cx, cy, maxDist * 0.3, cx, cy, maxDist);
      gradient.addColorStop(0, 'rgba(0,0,0,0)');
      gradient.addColorStop(1, `rgba(0,0,0,${vignetteIntensity / 100})`);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);
    }
  }, [intensity, tone, vignetteEnabled, vignetteIntensity, grainEnabled, grainIntensity, getSepiaMatrix]);

  useEffect(() => {
    if (!imgElement || !previewCanvasRef.current) return;
    applySepia(previewCanvasRef.current, imgElement);
  }, [imgElement, applySepia]);

  const handleDownload = useCallback(() => {
    if (!imgElement) return;
    const canvas = document.createElement('canvas');
    applySepia(canvas, imgElement);
    const mimeType = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'jpg' ? jpgQuality / 100 : undefined;
    const dataUrl = canvas.toDataURL(mimeType, quality);
    const link = document.createElement('a');
    const baseName = file?.name?.replace(/\.[^.]+$/, '') || 'sepia';
    link.download = `${baseName}-sepia.${outputFormat === 'jpg' ? 'jpg' : 'png'}`;
    link.href = dataUrl;
    link.click();
  }, [imgElement, applySepia, outputFormat, jpgQuality, file]);

  const handleReset = useCallback(() => {
    setFile(null);
    setOriginalPreview(null);
    setImgElement(null);
    setImageDimensions(null);
    setIntensity(80);
    setTone('classic');
    setVignetteEnabled(false);
    setVignetteIntensity(50);
    setGrainEnabled(false);
    setGrainIntensity(30);
    setError(null);
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Sun className="w-6 h-6 text-amber-600" />
          <h1 className="text-2xl font-bold text-gray-900">Sepia Image</h1>
        </div>
        <p className="text-gray-600">Apply vintage sepia tones with vignette and film grain effects.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {!file ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors"
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-1">Drop an image here or click to upload</p>
          <p className="text-sm text-gray-500">Supports PNG, JPG, WebP, GIF</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Controls */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Settings</h2>
              <button onClick={handleReset} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
            </div>

            {/* Tone selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sepia Tone</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {TONES.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => setTone(t.value)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      tone === t.value
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <div className="font-medium text-sm">{t.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Intensity slider */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Intensity</label>
                <span className="text-sm font-mono text-amber-600">{intensity}%</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={intensity}
                onChange={(e) => setIntensity(Number(e.target.value))}
                className="w-full accent-amber-600"
              />
            </div>

            {/* Vignette */}
            <div className="border border-gray-100 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="vignette"
                  checked={vignetteEnabled}
                  onChange={(e) => setVignetteEnabled(e.target.checked)}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <label htmlFor="vignette" className="text-sm font-medium text-gray-700">Vignette (dark corners)</label>
              </div>
              {vignetteEnabled && (
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Vignette Intensity</span>
                    <span className="font-mono text-amber-600">{vignetteIntensity}%</span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    value={vignetteIntensity}
                    onChange={(e) => setVignetteIntensity(Number(e.target.value))}
                    className="w-full accent-amber-600"
                  />
                </div>
              )}
            </div>

            {/* Grain */}
            <div className="border border-gray-100 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="grain"
                  checked={grainEnabled}
                  onChange={(e) => setGrainEnabled(e.target.checked)}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <label htmlFor="grain" className="text-sm font-medium text-gray-700">Film Grain / Noise</label>
              </div>
              {grainEnabled && (
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Grain Intensity</span>
                    <span className="font-mono text-amber-600">{grainIntensity}%</span>
                  </div>
                  <input
                    type="range"
                    min={5}
                    max={100}
                    value={grainIntensity}
                    onChange={(e) => setGrainIntensity(Number(e.target.value))}
                    className="w-full accent-amber-600"
                  />
                </div>
              )}
            </div>

            {/* Output format */}
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
                <select
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="png">PNG</option>
                  <option value="jpg">JPG</option>
                </select>
              </div>
              {outputFormat === 'jpg' && (
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quality: {jpgQuality}%
                  </label>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    value={jpgQuality}
                    onChange={(e) => setJpgQuality(Number(e.target.value))}
                    className="w-full accent-amber-600"
                  />
                </div>
              )}
            </div>

            {/* Before/After toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="beforeAfterSepia"
                checked={showBeforeAfter}
                onChange={(e) => setShowBeforeAfter(e.target.checked)}
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <label htmlFor="beforeAfterSepia" className="text-sm text-gray-700">Show before/after side-by-side</label>
            </div>
          </div>

          {/* Image info */}
          {imageDimensions && file && (
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1"><ImageIcon className="w-4 h-4" /> {imageDimensions.w} x {imageDimensions.h}px</span>
              <span>{formatFileSize(file.size)}</span>
              <span>{file.name}</span>
            </div>
          )}

          {/* Preview */}
          <div className={`grid gap-4 ${showBeforeAfter ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
            {showBeforeAfter && originalPreview && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Original</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                  <img src={originalPreview} alt="Original" className="w-full h-auto" />
                </div>
              </div>
            )}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Sepia</h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                <canvas ref={previewCanvasRef} className="w-full h-auto" />
              </div>
            </div>
          </div>

          {/* Download */}
          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium"
            >
              <Download className="w-5 h-5" /> Download Sepia Image
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SepiaImage;
